import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { collection, query, getDocs, doc, updateDoc, deleteDoc, addDoc, orderBy, where, limit, startAfter, DocumentData } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Organization } from '../../types/organization';
import { User } from '../../types/user';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  BuildingOfficeIcon, 
  PencilIcon, 
  TrashIcon,
  EyeIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  UserGroupIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import Modal from '../common/Modal';
import { OrganizationSettings } from '../settings/OrganizationSettings';
import DeleteOrganizationModal from '../settings/DeleteOrganizationModal';
import LoadingScreen from '../common/LoadingScreen';
import { formatDistanceToNow } from 'date-fns';
import { hu, enUS } from 'date-fns/locale';

interface OrganizationWithStats extends Organization {
  userCount: number;
  activeUserCount: number;
  lastActivity?: Date;
  subscriptionStatus?: 'active' | 'inactive' | 'trial' | 'expired';
  subscriptionPlan?: string;
}

const PAGE_SIZE = 20;

const SystemOrganizationManager: React.FC = () => {
  const { t, language } = useLanguage();
  const [organizations, setOrganizations] = useState<OrganizationWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [lastVisible, setLastVisible] = useState<DocumentData | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [subscriptionFilter, setSubscriptionFilter] = useState<string>('all');
  const [selectedOrg, setSelectedOrg] = useState<OrganizationWithStats | null>(null);
  const [orgStats, setOrgStats] = useState<{ userCount: number; activeUserCount: number; lastActivity?: Date } | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [totalLoaded, setTotalLoaded] = useState(0);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Form state for create/edit
  const [formData, setFormData] = useState({
    name: '',
    taxNumber: '',
    registrationNumber: '',
    address: {
      street: '',
      city: '',
      postalCode: '',
      country: 'Hungary'
    },
    contact: {
      name: '',
      email: '',
      phone: '',
      position: ''
    },
    isActive: true,
    subscriptionPlan: 'basic',
    subscriptionStatus: 'active' as 'active' | 'inactive' | 'trial' | 'expired'
  });

  // Debounce search
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchTerm]);

  // Fetch organizations with cursor pagination
  const fetchOrganizations = useCallback(async (reset: boolean = false) => {
    try {
      if (reset) {
        setLoading(true);
        setLastVisible(null);
      } else {
        setLoadingMore(true);
      }

      let q;
      const baseCollection = collection(db, 'organizations');

      if (debouncedSearch) {
        // Server-side search by name prefix
        const searchEnd = debouncedSearch + '\uf8ff';
        q = query(
          baseCollection,
          orderBy('name'),
          where('name', '>=', debouncedSearch),
          where('name', '<=', searchEnd),
          limit(PAGE_SIZE)
        );
      } else {
        // Default: most recent first
        q = query(
          baseCollection,
          orderBy('createdAt', 'desc'),
          limit(PAGE_SIZE)
        );
      }

      if (!reset && lastVisible) {
        q = query(q, startAfter(lastVisible));
      }

      const snapshot = await getDocs(q);
      const orgsData: OrganizationWithStats[] = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          userCount: 0,
          activeUserCount: 0,
          subscriptionStatus: data.subscriptionStatus || 'active',
          subscriptionPlan: data.subscriptionPlan || 'basic'
        } as OrganizationWithStats;
      });

      if (reset) {
        setOrganizations(orgsData);
      } else {
        setOrganizations(prev => [...prev, ...orgsData]);
      }

      setLastVisible(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMore(snapshot.docs.length === PAGE_SIZE);
      setTotalLoaded(prev => reset ? orgsData.length : prev + orgsData.length);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast.error('Hiba történt a szervezetek betöltésekor');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [debouncedSearch, lastVisible]);

  // Load on mount and when search changes
  useEffect(() => {
    fetchOrganizations(true);
  }, [debouncedSearch]);

  // Lazy load user stats for a single org
  const fetchOrgUserStats = async (orgId: string) => {
    setLoadingStats(true);
    try {
      const usersQuery = query(
        collection(db, 'users'),
        where('organizationId', '==', orgId)
      );
      const snapshot = await getDocs(usersQuery);
      const orgUsers = snapshot.docs.map(d => ({ id: d.id, ...d.data() })) as User[];
      const activeUsers = orgUsers.filter(u => (u as any).isActive !== false);
      
      const lastActivity = orgUsers.reduce((latest, user) => {
        const userLastLogin = (user as any).lastLoginAt?.toDate?.() || (user as any).lastLoginAt;
        if (userLastLogin && (!latest || userLastLogin > latest)) {
          return userLastLogin;
        }
        return latest;
      }, null as Date | null);

      const stats = {
        userCount: orgUsers.length,
        activeUserCount: activeUsers.length,
        lastActivity: lastActivity || undefined
      };
      setOrgStats(stats);

      // Update the org in the list too
      setOrganizations(prev => prev.map(o => 
        o.id === orgId ? { ...o, ...stats } : o
      ));
      return stats;
    } catch (error) {
      console.error('Error fetching org user stats:', error);
      return null;
    } finally {
      setLoadingStats(false);
    }
  };

  // Client-side filters (status & subscription) on loaded results
  const filteredOrganizations = useMemo(() => {
    return organizations.filter(org => {
      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'active' && org.isActive) ||
        (statusFilter === 'inactive' && !org.isActive);
      const matchesSubscription = subscriptionFilter === 'all' || 
        org.subscriptionStatus === subscriptionFilter;
      return matchesStatus && matchesSubscription;
    });
  }, [organizations, statusFilter, subscriptionFilter]);

  const handleView = (org: OrganizationWithStats) => {
    setSelectedOrg(org);
    setOrgStats(null);
    setIsViewModalOpen(true);
    fetchOrgUserStats(org.id);
  };

  const handleEdit = (org: OrganizationWithStats) => {
    setSelectedOrg(org);
    setFormData({
      name: org.name,
      taxNumber: org.taxNumber || '',
      registrationNumber: org.registrationNumber || '',
      address: org.address,
      contact: org.contact,
      isActive: org.isActive,
      subscriptionPlan: org.subscriptionPlan || 'basic',
      subscriptionStatus: org.subscriptionStatus || 'active'
    });
    setIsEditModalOpen(true);
  };

  const handleCreate = () => {
    setSelectedOrg(null);
    setFormData({
      name: '',
      taxNumber: '',
      registrationNumber: '',
      address: {
        street: '',
        city: '',
        postalCode: '',
        country: 'Hungary'
      },
      contact: {
        name: '',
        email: '',
        phone: '',
        position: ''
      },
      isActive: true,
      subscriptionPlan: 'basic',
      subscriptionStatus: 'active'
    });
    setIsCreateModalOpen(true);
  };

  const handleDelete = async (org: OrganizationWithStats) => {
    setSelectedOrg(org);
    setIsDeleteModalOpen(true);
    // Lazy load user count for delete check
    if (org.userCount === 0) {
      const stats = await fetchOrgUserStats(org.id);
      if (stats && stats.userCount > 0) {
        setSelectedOrg({ ...org, ...stats });
      }
    }
  };

  const confirmDelete = async () => {
    if (!selectedOrg) return;
    
    try {
      // Check if organization has users
      if (selectedOrg.userCount > 0) {
        toast.error('Nem törölhető olyan szervezet, amelyhez felhasználók tartoznak');
        return;
      }
      
      await deleteDoc(doc(db, 'organizations', selectedOrg.id));
      toast.success('Szervezet sikeresen törölve');
      setIsDeleteModalOpen(false);
      setSelectedOrg(null);
      fetchOrganizations(true);
    } catch (error) {
      console.error('Error deleting organization:', error);
      toast.error('Hiba történt a szervezet törlésekor');
    }
  };

  const handleSave = async () => {
    try {
      if (selectedOrg) {
        // Update existing organization
        const orgRef = doc(db, 'organizations', selectedOrg.id);
        await updateDoc(orgRef, {
          ...formData,
          updatedAt: new Date()
        });
        toast.success('Szervezet sikeresen frissítve');
        setIsEditModalOpen(false);
      } else {
        // Create new organization
        await addDoc(collection(db, 'organizations'), {
          ...formData,
          ownerId: '', // Will be set when first user joins
          users: [],
          createdAt: new Date(),
          updatedAt: new Date()
        });
        toast.success('Szervezet sikeresen létrehozva');
        setIsCreateModalOpen(false);
      }
      
      setSelectedOrg(null);
      fetchOrganizations(true);
    } catch (error) {
      console.error('Error saving organization:', error);
      toast.error('Hiba történt a szervezet mentésekor');
    }
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
        <CheckCircleIcon className="w-3 h-3 mr-1" />
        Aktív
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
        <XCircleIcon className="w-3 h-3 mr-1" />
        Inaktív
      </span>
    );
  };

  const getSubscriptionBadge = (status: string) => {
    const badges = {
      active: { color: 'green', text: 'Aktív előfizetés' },
      trial: { color: 'blue', text: 'Próbaidőszak' },
      expired: { color: 'red', text: 'Lejárt' },
      inactive: { color: 'gray', text: 'Inaktív' }
    };
    
    const badge = badges[status as keyof typeof badges] || badges.inactive;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${badge.color}-100 dark:bg-${badge.color}-900/30 text-${badge.color}-800 dark:text-${badge.color}-300`}>
        {badge.text}
      </span>
    );
  };

  const formatDate = (date: Date) => {
    const formatted = formatDistanceToNow(date, {
      addSuffix: true,
      locale: language === 'hu' ? hu : enUS
    });
    
    if (language === 'hu') {
      return formatted
        .replace('körülbelül ', '')
        .replace(' ezelőtt', '')
        .replace('órával', 'órája')
        .replace('nappal', 'napja')
        .replace('héttel', 'hete')
        .replace('hónappal', 'hónapja')
        .replace('évvel', 'éve')
        .replace('perccel', 'perce')
        .replace('másodperccel', 'másodperce');
    }
    
    return formatted;
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Szervezetek kezelése
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Betöltve: {totalLoaded} szervezet{hasMore ? ' (több is van)' : ''}
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          Új szervezet
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Keresés szervezet neve szerint..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
            />
          </div>
          
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="all">Minden státusz</option>
              <option value="active">Aktív</option>
              <option value="inactive">Inaktív</option>
            </select>
          </div>
          
          <div>
            <select
              value={subscriptionFilter}
              onChange={(e) => setSubscriptionFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="all">Minden előfizetés</option>
              <option value="active">Aktív előfizetés</option>
              <option value="trial">Próbaidőszak</option>
              <option value="expired">Lejárt</option>
              <option value="inactive">Inaktív</option>
            </select>
          </div>
          
          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
            <FunnelIcon className="w-4 h-4 mr-1" />
            {filteredOrganizations.length} találat
          </div>
        </div>
      </div>

      {/* Organizations Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Szervezet
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Kapcsolat
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Felhasználók
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Előfizetés
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Státusz
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Utolsó aktivitás
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Műveletek
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredOrganizations.map((org) => (
                <tr key={org.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                          <BuildingOfficeIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {org.name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {org.taxNumber && `Adószám: ${org.taxNumber}`}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {org.contact.name}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {org.contact.email}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-900 dark:text-gray-100">
                      <UserGroupIcon className="w-4 h-4 mr-1 text-gray-400" />
                      {org.userCount > 0 ? `${org.activeUserCount}/${org.userCount}` : '—'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      {getSubscriptionBadge(org.subscriptionStatus || 'active')}
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {org.subscriptionPlan || 'basic'}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(org.isActive)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {org.lastActivity ? (
                      <div className="flex items-center">
                        <CalendarIcon className="w-4 h-4 mr-1" />
                        {formatDate(org.lastActivity)}
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleView(org)}
                        className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(org)}
                        className="text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(org)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Load More */}
        {hasMore && (
          <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 text-center">
            <button
              onClick={() => fetchOrganizations(false)}
              disabled={loadingMore}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-md transition-colors disabled:opacity-50"
            >
              {loadingMore ? (
                <>
                  <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
                  Betöltés...
                </>
              ) : (
                <>
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Több betöltése
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Szervezet lapja – megtekintés + szerkesztés füleken (Cégadatok · Cím · Kapcsolattartó · Logó · Tagok · Előfizetés & rendszer) */}
      {selectedOrg && (isViewModalOpen || isEditModalOpen) && (
        <Modal isOpen onClose={() => { setIsViewModalOpen(false); setIsEditModalOpen(false); }} title={selectedOrg.name} size="4xl" noPadding>
          <div className="flex flex-col min-h-full">
            <div className="px-4 sm:px-6 pt-4 pb-3 border-b border-[var(--crm-border)] flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                {selectedOrg.logoUrl ? <img src={selectedOrg.logoUrl} alt="" className="h-10 w-10 object-contain" /> : <BuildingOfficeIcon className="h-5 w-5 text-gray-500 dark:text-gray-300" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-50 truncate">{selectedOrg.name}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 flex flex-wrap items-center gap-x-2">
                  {getStatusBadge(selectedOrg.isActive)}
                  <span>{selectedOrg.userCount || (selectedOrg.users?.length || 0) + 1} tag</span>
                  {orgStats?.lastActivity && <span>· utolsó aktivitás {formatDistanceToNow(orgStats.lastActivity, { addSuffix: true, locale: language === 'hu' ? hu : enUS })}</span>}
                  <span className="font-mono">· {selectedOrg.id}</span>
                </div>
              </div>
            </div>
            <div className="flex-1 p-4 sm:p-6">
              <OrganizationSettings
                organization={selectedOrg}
                isEditing={true}
                onEditingChange={() => {}}
                onOrganizationUpdate={(updated) => { setSelectedOrg({ ...selectedOrg, ...updated }); fetchOrganizations(true); }}
                hideButtons={true}
                onRequestDelete={(org) => { setSelectedOrg({ ...selectedOrg, ...org }); setIsDeleteModalOpen(true); }}
              />
            </div>
            <div className="mt-auto sticky bottom-0 z-10 border-t border-[var(--crm-border)] bg-[var(--crm-surface)] px-4 sm:px-6 py-3 [padding-bottom:max(0.75rem,env(safe-area-inset-bottom))] flex justify-end gap-2">
              <button type="button" onClick={() => { setIsViewModalOpen(false); setIsEditModalOpen(false); }} className="crm-btn crm-btn-ghost mr-auto">Bezárás</button>
              <button type="button" onClick={() => { const f = document.querySelector('#org-settings-form') as HTMLFormElement | null; f?.requestSubmit(); }} className="crm-btn crm-btn-primary">Mentés</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Új szervezet */}
      <Modal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} title="Új szervezet létrehozása" size="md">
        <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Szervezet neve <span className="text-rose-500">*</span></label>
            <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full h-10 border rounded-lg px-3 text-sm" autoFocus required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Adószám</label>
              <input type="text" value={formData.taxNumber} onChange={(e) => setFormData({ ...formData, taxNumber: e.target.value })} className="w-full h-9 border rounded-lg px-3 text-sm" placeholder="12345678-1-12" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Kapcsolattartó e-mail</label>
              <input type="email" value={formData.contact.email} onChange={(e) => setFormData({ ...formData, contact: { ...formData.contact, email: e.target.value } })} className="w-full h-9 border rounded-lg px-3 text-sm" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />Aktív</label>
          <p className="text-[11px] text-gray-400">A címet, logót, tagokat és előfizetést létrehozás után a szervezet lapján állíthatod be. Tulajdonos az első csatlakozó felhasználó lesz.</p>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => setIsCreateModalOpen(false)} className="crm-btn crm-btn-ghost">Mégse</button>
            <button type="submit" disabled={!formData.name.trim()} className="crm-btn crm-btn-primary">Létrehozás</button>
          </div>
        </form>
      </Modal>

      {/* Törlés – névmegerősítéssel, kaszkáddal */}
      <DeleteOrganizationModal
        organization={selectedOrg}
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onDeleted={() => { setIsDeleteModalOpen(false); setIsViewModalOpen(false); setIsEditModalOpen(false); setSelectedOrg(null); fetchOrganizations(true); }}
      />
    </div>
  );
};

export default SystemOrganizationManager;
